import { useState, useEffect, useCallback } from 'react';
import { getApiBase, fetchWithTimeout } from '../apiBase';

const HEALTH_TIMEOUT_MS = 4000;
const BACKEND_HEARTBEAT_MS = 30000;
const BACKEND_RECONNECT_MS = 5000;

function getHealthUrls() {
  const API_BASE = getApiBase();
  const base = API_BASE || '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const sameOriginHealth = base ? base + '/health' : (origin ? origin + '/health' : '/health');
  const fallbackPorts = [3001, 3002, 3003, 3004, 3005, 3006, 3007, 3008, 3009, 3010];
  const fallbackLocalhost = fallbackPorts.map((p) => `http://localhost:${p}/health`);
  const fallbackLoopback = fallbackPorts.map((p) => `http://127.0.0.1:${p}/health`);
  const h = typeof window !== 'undefined' ? window.location?.hostname : '';
  const isLocalhost = h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1';
  if (!isLocalhost) {
    return [sameOriginHealth];
  }
  const default3001 = ['http://127.0.0.1:3001/health', 'http://localhost:3001/health'];
  const preferSameOrigin = typeof origin === 'string' && /^https?:\/\//i.test(origin);
  let primary;
  if (preferSameOrigin && sameOriginHealth) {
    primary = [sameOriginHealth, ...default3001.filter((u) => u !== sameOriginHealth)];
  } else {
    primary = [...default3001, sameOriginHealth];
  }
  const extras = [...fallbackLoopback, ...fallbackLocalhost].filter(
    (u) => !primary.includes(u)
  );
  return [...primary, ...extras];
}

function tryUrl(url) {
  return fetchWithTimeout(url, { method: 'GET', timeout: HEALTH_TIMEOUT_MS })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('not ok'))))
    .then((d) => (d?.status === 'ok' ? true : Promise.reject(new Error('bad status'))));
}

function runHealthCheck(onDone) {
  if (typeof window !== 'undefined' && window.electronAPI?.healthCheck) {
    window.electronAPI.healthCheck()
      .then((r) => onDone(!!(r && r.ok)))
      .catch(() => onDone(false));
    return;
  }
  const urls = getHealthUrls();
  let idx = 0;
  function tryNext() {
    if (idx >= urls.length) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[Aria] Backend health check failed. Tried:', urls, '- Ensure backend is running: cd HowTo-Backend && npm start, then open http://localhost:3001');
      }
      onDone(false);
      return;
    }
    const url = urls[idx];
    idx += 1;
    tryUrl(url)
      .then(() => onDone(true))
      .catch(() => tryNext());
  }
  tryNext();
}

/**
 * Polls backend health endpoint. Returns { backendConnected, backendReachable }.
 * Polls every 5s when down, 30s when up. Avoids re-renders when status doesn't change.
 */
export default function useBackendHealth() {
  // Start in an 'unknown' (null) state, not false. The error banners render
  // only on a strict `=== false`, so an unknown start keeps them hidden until
  // a health check *actually* fails — otherwise every healthy cold load flashed
  // two red "Cannot reach server" banners for up to 4s before the first /health
  // check resolved. null = not-yet-checked, true = up, false = confirmed down.
  const [backendConnected, setBackendConnected] = useState(null);
  const [backendReachable, setBackendReachable] = useState(null);

  const applyBackendStatus = useCallback((ok) => {
    setBackendConnected((prev) => {
      if (ok) return prev === true ? prev : true;
      return prev === false ? prev : false;
    });
    setBackendReachable((prev) => {
      if (ok) return prev === true ? prev : true;
      return prev === false ? prev : false;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    const schedule = (delayMs) => {
      if (timeoutId != null) clearTimeout(timeoutId);
      timeoutId = setTimeout(tick, delayMs);
    };

    const tick = () => {
      runHealthCheck((ok) => {
        if (cancelled) return;
        applyBackendStatus(ok);
        schedule(ok ? BACKEND_HEARTBEAT_MS : BACKEND_RECONNECT_MS);
      });
    };

    tick();
    return () => {
      cancelled = true;
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [applyBackendStatus]);

  return { backendConnected, backendReachable };
}
